import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from '../features/auth/contexts/AuthContext';
import { userAPI } from '../shared/services/api';

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const ProfileCard = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
`;

const ProfileHeader = styled.div`
  padding: 2rem;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  text-align: center;
`;

const ProfileTitle = styled.h1`
  font-family: var(--font-serif);
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-gray-900);
  margin-bottom: 0.5rem;
`;

const ProfileSubtitle = styled.p`
  color: var(--color-gray-600);
  font-size: 1.1rem;
`;

const ProfileForm = styled.form`
  padding: 2rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--color-gray-200);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--color-gray-700);
  font-family: var(--font-sans);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-family: var(--font-sans);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  &:disabled {
    background: var(--color-gray-100);
    color: var(--color-gray-500);
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-family: var(--font-sans);
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SaveButton = styled.button<{ loading?: boolean }>`
  padding: 0.75rem 1.5rem;
  background: var(--color-primary);
  color: var(--color-gray-800);
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-sans);
  cursor: pointer;
  transition: background-color 0.2s ease;
  opacity: ${props => props.loading ? 0.7 : 1};

  &:hover:not(:disabled) {
    background: #8BB5E0;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--color-gray-200);
  color: var(--color-gray-700);
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-sans);
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--color-gray-300);
  }
`;

const InfoSection = styled.div`
  background: var(--color-gray-50);
  padding: 1.5rem;
  border-radius: var(--border-radius-md);
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--color-gray-200);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: var(--color-gray-600);
`;

const InfoValue = styled.span`
  color: var(--color-gray-800);
  font-weight: 500;
`;

interface ProfileFormData {
  firstName: string;
  lastName: string;
  bio: string;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      bio: user?.bio || ''
    }
  });

  // Watch for changes
  const formValues = watch();
  useEffect(() => {
    const hasFormChanges = 
      formValues.firstName !== (user?.firstName || '') ||
      formValues.lastName !== (user?.lastName || '') ||
      formValues.bio !== (user?.bio || '');
    setHasChanges(hasFormChanges);
  }, [formValues, user]);

  const onSubmit = async (data: ProfileFormData) => {
    setSaving(true);
    try {
      await userAPI.updateProfile(data);
      toast.success('Profile updated successfully!');
      setHasChanges(false);
      
      // You might want to refresh the user data here
      // In a real app, you'd probably want to update the user context
      
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    reset({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      bio: user?.bio || ''
    });
    setHasChanges(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!user) {
    return (
      <ProfileContainer>
        <ProfileCard>
          <ProfileHeader>
            <ProfileTitle>Profile Not Available</ProfileTitle>
            <ProfileSubtitle>Please log in to view your profile.</ProfileSubtitle>
          </ProfileHeader>
        </ProfileCard>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <ProfileCard>
        <ProfileHeader>
          <ProfileTitle>My Profile</ProfileTitle>
          <ProfileSubtitle>Manage your BookKids account settings</ProfileSubtitle>
        </ProfileHeader>

        <ProfileForm onSubmit={handleSubmit(onSubmit)}>
          <FormSection>
            <SectionTitle>Account Information</SectionTitle>
            <InfoSection>
              <InfoItem>
                <InfoLabel>Username</InfoLabel>
                <InfoValue>{user.username}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{user.email}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Member Since</InfoLabel>
                <InfoValue>{formatDate(user.createdAt)}</InfoValue>
              </InfoItem>
            </InfoSection>
            <p style={{ color: 'var(--color-gray-500)', fontSize: '0.9rem' }}>
              Username and email are managed through your account settings and cannot be changed here.
            </p>
          </FormSection>

          <FormSection>
            <SectionTitle>Personal Information</SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label>First Name</Label>
                <Input
                  {...register('firstName', { 
                    maxLength: { value: 100, message: 'First name must be less than 100 characters' }
                  })}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <span style={{ color: '#EF4444', fontSize: '0.85rem' }}>
                    {errors.firstName.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  {...register('lastName', { 
                    maxLength: { value: 100, message: 'Last name must be less than 100 characters' }
                  })}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <span style={{ color: '#EF4444', fontSize: '0.85rem' }}>
                    {errors.lastName.message}
                  </span>
                )}
              </FormGroup>
            </FormGrid>

            <FormGroup style={{ marginTop: '1rem' }}>
              <Label>Bio</Label>
              <TextArea
                {...register('bio', { 
                  maxLength: { value: 1000, message: 'Bio must be less than 1000 characters' }
                })}
                placeholder="Tell other BookKids users about yourself..."
              />
              {errors.bio && (
                <span style={{ color: '#EF4444', fontSize: '0.85rem' }}>
                  {errors.bio.message}
                </span>
              )}
              <span style={{ color: 'var(--color-gray-500)', fontSize: '0.85rem' }}>
                {watch('bio')?.length || 0}/1000 characters
              </span>
            </FormGroup>
          </FormSection>

          <ButtonGroup>
            <CancelButton
              type="button"
              onClick={handleCancel}
              disabled={!hasChanges || saving}
            >
              Cancel
            </CancelButton>
            <SaveButton
              type="submit"
              loading={saving}
              disabled={!hasChanges || saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </SaveButton>
          </ButtonGroup>
        </ProfileForm>
      </ProfileCard>
    </ProfileContainer>
  );
};

export default Profile;